import i18next from 'i18next';
import { AppProps } from '../../../@types/AppProps';
import { Vehicle } from '../../../@types/Vehicle';
import ButtonArrowRight from '../../../Components/Button/ButtonArrowRight';
import HtmlNode from '../../../Components/Extension/HtmlNode';
import InputFieldEdit from '../../../Components/Input/InputFieldEdit';
import getVehicle, { getVehicleErrorMessage } from '../../../Http/getVehicle';
import { RequestError } from '../../../Http/http';
import Alert from '../../../Templates/Alert';
import validation from './validation';
import { MarketCode } from '../../../@types/MarketCode';

const INPUT_REGISTRATION_NUMBER = 'input-registration-number-modal';
const INPUT_REGISTRATION_NUMBER_NODE = `${INPUT_REGISTRATION_NUMBER}-node`;

const BUTTON_FETCH_NODE = 'button-fetch-node';

export interface RegistrationNumberState {
  registrationNumber: string;
}

interface RegistrationNumberStateLocal {
  value: {
    registrationNumber: string;
  };
  validation: {
    registrationNumber: boolean;
  };
  interaction: {
    registrationNumber: boolean;
  };
}

type NameTypes = keyof RegistrationNumberStateLocal['value'];

const createState = (
  _value: RegistrationNumberState,
  marketCode?: MarketCode
): RegistrationNumberStateLocal => {
  const value = { ..._value };
  return {
    value,
    validation: {
      registrationNumber: validation.registrationNumber(value.registrationNumber, marketCode),
    },
    interaction: { registrationNumber: false },
  };
};

interface RegistrationNumberProps {
  state: RegistrationNumberState;
  vehicle?: Vehicle;
  settings: AppProps;
  onUpdate: (registrationNumber: string, vehicle?: Vehicle) => void;
  onEdit: () => void;
}

class RegistrationNumber extends HtmlNode {
  private readonly props: RegistrationNumberProps;
  private contexts: {
    registrationNumber?: InputFieldEdit;
    buttonFetch?: ButtonArrowRight;
  } = {};
  private state: RegistrationNumberStateLocal;
  private requestError?: RequestError;
  private vehicle?: Vehicle;

  constructor(element: HTMLElement | null, props: RegistrationNumberProps) {
    super(element);
    this.props = props;
    this.state = createState(props.state, props.settings.marketCode);
    this.vehicle = this.props.vehicle;

    this.render();
  }

  private onEdit() {
    this.props.onEdit();
  }

  private onChange(e: Event) {
    const currentTarget = e.currentTarget as HTMLInputElement;
    const name = currentTarget.name as NameTypes;
    const value = currentTarget.value.toUpperCase();

    this.state.value[name] = value;
    currentTarget.value = value;

    this.state.validation[name] = validation[name](value, this.props.settings.marketCode);
    this.updateUiError(name);
    this.updateProceedButton();
  }

  private onBlur(e: Event) {
    const currentTarget = e.currentTarget as HTMLInputElement;
    const name = currentTarget.name as NameTypes;

    this.state.interaction[name] = true;

    this.state.validation[name] = validation[name](
      this.state.value[name],
      this.props.settings.marketCode
    );
    this.updateUiError(name);
    this.updateProceedButton();
  }

  private onKeyDown(e: KeyboardEvent) {
    if (e.key == 'Enter') {
      if (this.state.validation.registrationNumber) {
        this.onGetVehicle();
      } else {
        this.onBlur(e);
      }
    }
  }

  private updateUiError(name: NameTypes) {
    this.contexts[name]?.setError(this.state.interaction[name] && !this.state.validation[name]);
  }

  private updateProceedButton() {
    this.contexts.buttonFetch?.disabled(!this.state.validation.registrationNumber);
  }

  async onGetVehicle() {
    this.requestError = undefined;
    this.render();
    try {
      this.contexts.buttonFetch?.loading(true);
      const response = await getVehicle(
        this.props.settings.url,
        this.state.value.registrationNumber,
        this.props.settings.marketCode
      );
      this.vehicle = response;
      this.props.onUpdate(this.state.value.registrationNumber, this.vehicle);
    } catch (e) {
      this.requestError = e as RequestError;
    } finally {
      this.contexts.buttonFetch?.loading(false);
      this.render();
    }
  }

  render() {
    this.node.innerHTML = `
      <div class="waykevaluation-stack waykevaluation-stack--3" id="${INPUT_REGISTRATION_NUMBER_NODE}"></div>
      <div class="waykevaluation-stack waykevaluation-stack--3" id="${BUTTON_FETCH_NODE}"></div>
      ${
        this.requestError
          ? `<div class="waykevaluation-stack waykevaluation-stack--3">${Alert({
              tone: 'error',
              children: getVehicleErrorMessage(this.requestError.status),
            })}</div>`
          : ''
      }
    `;

    this.contexts.registrationNumber = new InputFieldEdit(
      document.querySelector<HTMLDivElement>(`#${INPUT_REGISTRATION_NUMBER_NODE}`),
      {
        title: i18next.t('input.registrationNumber.label'),
        value: this.state.value.registrationNumber,
        id: INPUT_REGISTRATION_NUMBER,
        error:
          this.state.interaction.registrationNumber && !this.state.validation.registrationNumber,
        errorMessage: i18next.t('input.registrationNumber.error') || undefined,
        name: 'registrationNumber',
        autocomplete: 'off',
        change: i18next.t('glossary.change') || undefined,
        placeholder: i18next.t('input.registrationNumber.placeholder') || undefined,
        readOnly: !!this.vehicle,
        market: this.props.settings.marketCode,
        onChange: (e) => this.onChange(e),
        onBlur: (e) => this.onBlur(e),
        onKeyDown: (e) => this.onKeyDown(e),
        onEdit: this.vehicle ? () => this.onEdit() : undefined,
      }
    );

    if (!this.vehicle) {
      this.contexts.buttonFetch = new ButtonArrowRight(
        document.querySelector<HTMLDivElement>(`#${BUTTON_FETCH_NODE}`),
        {
          title: i18next.t('glossary.next'),
          disabled: !this.state.validation.registrationNumber,
          onClick: () => this.onGetVehicle(),
        }
      );
    }
  }
}

export default RegistrationNumber;
